import React from "react";
import { FormControl, FormLabel, Switch, Stack, Option, Box, Sheet } from "@mui/joy";
import { SCreate, SDelete } from "../../../../styles/StyledMUI";
import { colorTokens } from "../../../../styles/globalStyles";
import CommonModal from "../../../common/CommonModal";
import DarkInput from "../../../common/DarkInput";
import DarkSelect from "../../../common/DarkSelect";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import type {
  Product,
  ProductRequest,
  ProductLineRequest,
  Prestation,
} from "../../../../api/billing/Billing.typed";
import {
  TAX_RATES,
  DEFAULT_TAX_RATE,
  formatPrice,
  formatNum,
  htToTtc,
  ttcToHt,
} from "../../shared/billingUtils";
import GroupAutocomplete from "../../shared/GroupAutocomplete";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (req: ProductRequest) => Promise<unknown>;
  initial?: Product | null;
  prestations: Prestation[];
};

type LineState = {
  key: number;
  prestation_id: number;
  quantity: number;
  override_price: number | null;
};

let lineKeyCounter = 0;

function emptyLine(): LineState {
  return { key: ++lineKeyCounter, prestation_id: 0, quantity: 1, override_price: null };
}

export default function ProductFormModal({ open, onClose, onSubmit, initial, prestations }: Props) {
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [groupName, setGroupName] = React.useState("");
  const [price, setPrice] = React.useState<number>(0);
  const [ttcInput, setTtcInput] = React.useState("");
  const [currency, setCurrency] = React.useState("EUR");
  const [taxRate, setTaxRate] = React.useState<number>(DEFAULT_TAX_RATE);
  const [credits, setCredits] = React.useState<number>(0);
  const [isActive, setIsActive] = React.useState(true);
  const [isUnlimited, setIsUnlimited] = React.useState(false);
  const [lines, setLines] = React.useState<LineState[]>([emptyLine()]);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open && initial) {
      setName(initial.Name);
      setDescription(initial.Description);
      setGroupName(initial.GroupName ?? "");
      setPrice(initial.Price);
      setTtcInput(formatNum(htToTtc(initial.Price, initial.TaxRate ?? DEFAULT_TAX_RATE)));
      setCurrency(initial.Currency || "EUR");
      setTaxRate(initial.TaxRate ?? DEFAULT_TAX_RATE);
      setCredits(initial.Credits);
      setIsActive(initial.IsActive);
      setIsUnlimited(initial.IsUnlimited);
      setLines(
        initial.Lines.length > 0
          ? initial.Lines.map((l) => {
              const prest = prestations.find((p) => p.ID === l.PrestationID);
              return {
                key: ++lineKeyCounter,
                prestation_id: l.PrestationID,
                quantity: l.Quantity,
                override_price: l.OverridePrice ?? prest?.UnitPrice ?? null,
              };
            })
          : [emptyLine()],
      );
    } else if (open) {
      setName("");
      setDescription("");
      setGroupName("");
      setPrice(0);
      setTtcInput("0");
      setCurrency("EUR");
      setTaxRate(DEFAULT_TAX_RATE);
      setCredits(0);
      setIsActive(true);
      setIsUnlimited(false);
      setLines([emptyLine()]);
    }
  }, [open, initial]);

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key: number) => setLines((prev) => prev.filter((l) => l.key !== key));
  const updateLine = (key: number, patch: Partial<LineState>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const indicativeTotal = React.useMemo(() => {
    return lines.reduce((sum, l) => {
      if (l.override_price !== null) return sum + l.override_price * l.quantity;
      const prest = prestations.find((p) => p.ID === l.prestation_id);
      return sum + (prest?.UnitPrice ?? 0) * l.quantity;
    }, 0);
  }, [lines, prestations]);

  React.useEffect(() => {
    if (indicativeTotal > 0) {
      const ht = Math.round(indicativeTotal * 100) / 100;
      setPrice(ht);
      setTtcInput(formatNum(htToTtc(ht, taxRate)));
    }
  }, [indicativeTotal]);

  const handleConfirm = async () => {
    setSubmitting(true);
    const reqLines: ProductLineRequest[] = lines
      .filter((l) => l.prestation_id > 0)
      .map((l) => ({
        prestation_id: l.prestation_id,
        quantity: l.quantity,
        override_price: l.override_price,
      }));
    await onSubmit({
      name,
      description,
      group_name: groupName,
      price,
      currency,
      tax_rate: taxRate,
      credits,
      is_active: isActive,
      is_unlimited: isUnlimited,
      lines: reqLines,
    });
    setSubmitting(false);
    onClose();
  };

  const isValid = name.trim().length > 0;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={initial ? "Modifier le produit" : "Nouveau produit"}
      size="xl"
      fitContentHeight
      onConfirm={handleConfirm}
      confirmLabel={initial ? "Enregistrer" : "Créer"}
      submitDisabled={!isValid}
      loading={submitting}
    >
      <Stack spacing={2}>
        <FormControl required>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Nom</FormLabel>
          <DarkInput value={name} onChange={(e) => setName(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Description</FormLabel>
          <DarkInput value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormControl>
        <GroupAutocomplete value={groupName} onChange={setGroupName} entity="products" />
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: 200 }}>
            <FormLabel sx={{ color: colorTokens.text.muted }}>TVA</FormLabel>
            <DarkSelect
              value={taxRate}
              onChange={(_, v) => {
                if (v == null) return;
                const rate = v as number;
                setTaxRate(rate);
                setTtcInput(formatNum(htToTtc(price, rate)));
              }}
            >
              {TAX_RATES.map((r) => (
                <Option key={r.value} value={r.value}>
                  {r.label}
                </Option>
              ))}
            </DarkSelect>
          </FormControl>
          <FormControl sx={{ width: 120 }}>
            <FormLabel sx={{ color: colorTokens.text.muted }}>Devise</FormLabel>
            <DarkSelect value={currency} onChange={(_, v) => v != null && setCurrency(v as string)}>
              <Option value="EUR">EUR</Option>
              <Option value="USD">USD</Option>
              <Option value="GBP">GBP</Option>
              <Option value="CHF">CHF</Option>
            </DarkSelect>
          </FormControl>
        </Stack>
        <Sheet
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            border: `1px solid ${colorTokens.border.default}`,
            borderRadius: "8px",
            p: 2,
          }}
        >
          <DarkTypography level="title-sm" sx={{ mb: 1.5 }}>
            Calculette de prix
          </DarkTypography>
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <FormControl required sx={{ flex: 1 }}>
              <FormLabel sx={{ color: colorTokens.text.muted }}>Prix de vente HT</FormLabel>
              <DarkInput
                type="number"
                slotProps={{ input: { step: "0.01", min: "0" } }}
                value={price}
                onChange={(e) => {
                  const ht = parseFloat(e.target.value) || 0;
                  setPrice(ht);
                  setTtcInput(formatNum(htToTtc(ht, taxRate)));
                }}
              />
            </FormControl>
            <DarkTypography level="body-lg" sx={{ pb: 0.5 }}>
              ⇄
            </DarkTypography>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel sx={{ color: colorTokens.text.muted }}>Prix de vente TTC</FormLabel>
              <DarkInput
                type="number"
                slotProps={{ input: { step: "0.01", min: "0" } }}
                value={ttcInput}
                onChange={(e) => {
                  setTtcInput(e.target.value);
                  const ttc = parseFloat(e.target.value) || 0;
                  setPrice(ttcToHt(ttc, taxRate));
                }}
              />
            </FormControl>
          </Stack>
          <DarkTypography level="body-xs" colorVariant="muted" sx={{ mt: 1 }}>
            Seul le prix HT est enregistré. Le TTC est calculé avec la TVA sélectionnée.
          </DarkTypography>
        </Sheet>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <FormControl sx={{ width: 160 }}>
            <FormLabel sx={{ color: colorTokens.text.muted }}>Crédits</FormLabel>
            <DarkInput
              type="number"
              slotProps={{ input: { min: "0" } }}
              value={credits}
              onChange={(e) => setCredits(parseInt(e.target.value) || 0)}
              disabled={isUnlimited}
            />
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 0.5 }}>
            <Switch checked={isUnlimited} onChange={(e) => setIsUnlimited(e.target.checked)} />
            <DarkTypography level="body-sm">Illimité</DarkTypography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 0.5 }}>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <DarkTypography level="body-sm">Actif</DarkTypography>
          </Box>
        </Stack>

        <Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}
          >
            <DarkTypography level="title-sm">Lignes</DarkTypography>
            <IconButtonCTA aria-label="Ajouter une ligne" size="sm" onClick={addLine}>
              <SCreate />
            </IconButtonCTA>
          </Box>
          <Sheet
            sx={{
              backgroundColor: colorTokens.bg.surfaceMuted,
              border: `1px solid ${colorTokens.border.default}`,
              borderRadius: "8px",
              p: 1.5,
            }}
          >
            <Stack spacing={1}>
              {lines.map((line) => (
                <Stack key={line.key} direction="row" spacing={1} alignItems="flex-end">
                  <FormControl sx={{ flex: 2 }}>
                    <FormLabel sx={{ color: colorTokens.text.muted, fontSize: "0.75rem" }}>
                      Prestation
                    </FormLabel>
                    <DarkSelect
                      value={line.prestation_id || undefined}
                      placeholder="Choisir..."
                      onChange={(_, v) => {
                        if (v == null) return;
                        const pid = v as number;
                        const prest = prestations.find((p) => p.ID === pid);
                        updateLine(line.key, {
                          prestation_id: pid,
                          override_price: prest?.UnitPrice ?? null,
                        });
                      }}
                    >
                      {prestations
                        .filter((p) => p.IsActive)
                        .map((p) => (
                          <Option key={p.ID} value={p.ID}>
                            {p.Name} (
                            {formatPrice(
                              htToTtc(p.UnitPrice, p.TaxRate ?? DEFAULT_TAX_RATE),
                              p.Currency,
                            )}{" "}
                            TTC)
                          </Option>
                        ))}
                    </DarkSelect>
                  </FormControl>
                  <FormControl sx={{ width: 80 }}>
                    <FormLabel sx={{ color: colorTokens.text.muted, fontSize: "0.75rem" }}>
                      Qté
                    </FormLabel>
                    <DarkInput
                      type="number"
                      slotProps={{ input: { min: "1" } }}
                      value={line.quantity}
                      onChange={(e) =>
                        updateLine(line.key, { quantity: parseInt(e.target.value) || 1 })
                      }
                    />
                  </FormControl>
                  <FormControl sx={{ width: 120 }}>
                    <FormLabel sx={{ color: colorTokens.text.muted, fontSize: "0.75rem" }}>
                      Prix HT
                    </FormLabel>
                    <DarkInput
                      type="number"
                      slotProps={{ input: { step: "0.01", min: "0" } }}
                      value={line.override_price ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        updateLine(line.key, { override_price: v === "" ? null : parseFloat(v) });
                      }}
                    />
                  </FormControl>
                  <IconButtonCTA
                    aria-label="Supprimer la ligne"
                    size="sm"
                    ctaVariant="danger"
                    onClick={() => removeLine(line.key)}
                    disabled={lines.length <= 1}
                  >
                    <SDelete />
                  </IconButtonCTA>
                </Stack>
              ))}
            </Stack>
          </Sheet>
          <DarkTypography level="body-xs" colorVariant="muted" sx={{ mt: 1, textAlign: "right" }}>
            Total indicatif (lignes) : {formatPrice(indicativeTotal)}
          </DarkTypography>
        </Box>
      </Stack>
    </CommonModal>
  );
}
